import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useShallow } from "zustand/shallow";

import { useUserStore } from "./state/store";
import { renderIcon } from "../icons";
import { normalizeIdentityEmail } from "../utils";
import { IoEyeOff, IoEye } from "react-icons/io5";
import {
  GiBearFace,
  GiCarousel,
  GiChipsBag,
  GiJoystick,
  GiVendingMachine,
  GiWaterBottle,
} from "react-icons/gi";
import { FaChargingStation } from "react-icons/fa";

import VendLogo from "../assets/logo/VendistriLogo.png";
import { PasswordModal } from "./PasswordModal";
import { marketingSiteUrl } from "../marketingSiteUrl";
import { authContinuation, signupPath } from "./authContinuation";
import "./signin.css";

function ClawMachineIcon({ className = "" }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      className={className}
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3 4h26M16 4v6"
        stroke="currentColor"
        strokeWidth="3.2"
        strokeLinecap="round"
      />
      <circle cx="16" cy="14" r="4.5" stroke="currentColor" strokeWidth="3.2" />
      <path
        d="M12.7 16.8 8.5 18.5 4 26.5 7.5 29 11.5 22M19.3 16.8l4.2 1.7 4.5 8-3.5 2.5-4-7"
        stroke="currentColor"
        strokeWidth="3.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const industryIcons = [
  { icon: GiVendingMachine, label: "Vending" },
  { icon: ClawMachineIcon, label: "Claw machines" },
  { icon: GiJoystick, label: "Arcades" },
  { icon: GiCarousel, label: "Amusements" },
  { icon: FaChargingStation, label: "EV charging" },
  { icon: GiChipsBag, label: "Snacks" },
  { icon: GiWaterBottle, label: "Beverages" },
  { icon: GiBearFace, label: "Plush" },
];

export default function SigninPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signin, loading, initialized, isAuthenticated } = useUserStore(
    useShallow((s) => ({
      signin: s.signin,
      loading: s.loading,
      initialized: s.initialized,
      isAuthenticated: s.isAuthenticated,
    }))
  );

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resetOpen, setResetOpen] = useState(false);

  const submitting = loading?.on;
  const redirectTo = authContinuation(location.search);
  const destination = redirectTo && redirectTo.split(/[?#]/, 1)[0] !== "/" ? redirectTo : "/dashboard";
  const createAccountUrl = marketingSiteUrl(signupPath(redirectTo));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!initialized || submitting) return;
    setError(null);
    if (!email.trim() || !password.trim()) {
      setError("Email and password are required");
      return;
    }
    try {
      const normalizedEmail = normalizeIdentityEmail(email);
      setEmail(normalizedEmail);
      const success = await signin(normalizedEmail, password);
      if (success) {
        navigate(destination, { replace: true });
      } else {
        setError("Invalid email or password");
      }
    } catch (err: any) {
      setError(err?.message || "Failed to sign in");
    }
  };

  if (!initialized) return null;

  if (isAuthenticated) {
    return <Navigate to={destination} replace />;
  }

  return (
    <div className={`signin-page${location.state?.signedOut ? " signin-page--signed-out" : ""}`}>
      <div className="signin-ribbon" aria-hidden="true">
        <span className="signin-ribbon-layer signin-ribbon-layer--one" />
        <span className="signin-ribbon-layer signin-ribbon-layer--two" />
        <span className="signin-ribbon-layer signin-ribbon-layer--three" />
        <span className="signin-ribbon-glow" />
      </div>

      <header className="signin-header">
        <img
          src={VendLogo}
          alt="Vendistri"
          className="signin-logo"
        />
      </header>

      <main className="signin-main">
        <section className="signin-story" aria-label="Built for unattended retail">
          <div className="signin-story-copy">
            <span className="signin-eyebrow">One platform. Every machine.</span>
            <h2>Keep every route, refill, and collection moving.</h2>
          </div>
        </section>

        <section className="signin-card" aria-labelledby="signin-heading">
          <div className="signin-card-heading">
            <span className="signin-card-kicker">Welcome back</span>
            <h1 id="signin-heading">Sign in to Vendistri</h1>
            <p>Access your operations portal</p>
          </div>
          <form className="signin-form" onSubmit={handleSubmit}>
            <label className="signin-field" htmlFor="signin-email">
              <span>Email</span>
              <input
                id="signin-email"
                type="email"
                autoComplete="username"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </label>
            <div className="signin-field">
              <span className="signin-password-label">
                <label htmlFor="signin-password">Password</label>
                <button type="button" onClick={() => setResetOpen(true)}>
                  Forgot password?
                </button>
              </span>
              <div className="signin-password-input">
                <input
                  id="signin-password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="signin-password-toggle"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {renderIcon(showPassword ? IoEyeOff : IoEye, "h-5 w-5")}
                </button>
              </div>
            </div>
            {location.state?.passwordReset && (
              <p role="status">Password reset. Sign in with your new password.</p>
            )}
            {error && (
              <div className="signin-error" role="alert">
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={!initialized || submitting}
              className="signin-submit"
            >
              <span>{submitting ? "Signing in…" : "Sign in"}</span>
              <span aria-hidden="true">→</span>
            </button>
          </form>
          <div className="signin-signup">
            <span>New to Vendistri?</span>
            <a href={createAccountUrl}>Create an account</a>
          </div>
        </section>
      </main>

      <footer className="signin-page-footer">
        <div className="signin-industry-grid" aria-hidden="true">
          {industryIcons.map(({ icon, label }, index) => (
            <span
              className="signin-industry-icon"
              style={{ "--icon-index": index } as React.CSSProperties}
              key={label}
            >
              {renderIcon(icon, "h-6 w-6")}
            </span>
          ))}
        </div>
        <div className="signin-legal">
          <span>All Rights Reserved © Vendistri</span>
          <nav aria-label="Legal links">
            <a href={marketingSiteUrl("/privacy.html")}>Privacy Policy</a>
            <a href={marketingSiteUrl("/terms.html")}>Terms of Use</a>
          </nav>
        </div>
      </footer>

      <PasswordModal
        open={resetOpen}
        initialMode="reset"
        emailMode="editable"
        initialEmail={email}
        onClose={() => setResetOpen(false)}
      />
    </div>
  );
}
